import { VideoPlayerHtml5 } from './html5/VideoPlayerHtml5';
import { VideoPlayerVimeo } from './vimeo/VideoPlayerVimeo';
import { VideoPlayerYoutube } from './youtube/VideoPlayerYoutube';

/**
 * Render a video player into the given Element.
 * Supports YouTube, Vimeo, and HTML5 Video
 */
export default class VideoPlayer {
	/**
	 * The element to which to render the video player
	 * @param {Element} element
	 */
	constructor(element, modalService = null) {
		/**
		 * The element to which to render the video player
		 * @type {Element}
		 */
		this.element = element;
		/**
		 * The volume level where 0 is muted and 1 is full volume
		 * @type {Number}
		 */
		this.volume = 1;
		/**
		 * The flavor of player
		 * @type {VideoPlayerHtml5|VideoPlayerVimeo|VideoPlayerYoutube}
		 */
		this.player = undefined;
		/**
		 * The name of the type (html5|vimeo|youtube)
		 * @type {String}
		 */
		this.type = undefined;
		/**
		 * Reference to a ModalService instance
		 */
		this.modalService = modalService;
	}

	/**
	 * Is the given URL playable
	 * @static
	 * @param {String} url  The URL to check support
	 * @returns {Boolean}  True if this player can play that URL
	 */
	static isPlayable(url) {
		return VideoPlayerYoutube.isPlayable(url) || VideoPlayerVimeo.isPlayable(url) || VideoPlayerHtml5.isPlayable(url);
	}

	/**
	 * Extract a playable URL from the given URL
	 * @param {Object} record  A post from one of these:
	 * 		a post from GET /posts with a youtube/vimeo source_link,
	 * 		a post from GET /posts with a video attachment,
	 * 		a file from GET /files/search,
	 * 		a file or link from GET /videos/search
	 * @param {Object} options  A way to extract video information
	 * @property {String} heading  The video title
	 * @returns {String}  The URL
	 */
	static getPlayableUrl(record, options = {}) {
		if (!record) {
			return undefined;
		}
		if (record.source_link && this.isPlayable(record.source_link)) {
			// post from GET /posts
			if (!options.heading) {
				options.heading = record.headline;
			}
			return record.source_link;
		} else if (record.href && VideoPlayerHtml5.isPlayable(record.href)) {
			// mp4 file record from GET /files/search
			if (!options.heading) {
				options.heading = record.name;
			}
			return record.href;
		} else if (record.videos && record.videos.length && VideoPlayerHtml5.isPlayable(record.videos[0].url)) {
			// post with mp4 attachment from GET /v3/posts/search
			if (!options.heading) {
				options.heading = record.videos[0].title;
			}
			return record.videos[0].url;
		} else if (record.url && this.isPlayable(record.url)) {
			// file or link from /videos/search
			if (!options.heading) {
				options.heading = record.name;
			}
			return record.url;
		}
		return undefined;
	}

	/**
	 * Play the given url
	 * @param {String} url
	 * @returns {Promise}  The promise resolves when the video ends
	 */
	play(url) {
		if (VideoPlayerYoutube.isPlayable(url)) {
			this.player = new VideoPlayerYoutube(this.element);
			this.type = 'youtube';
		} else if (VideoPlayerVimeo.isPlayable(url)) {
			this.player = new VideoPlayerVimeo(this.element);
			this.type = 'vimeo';
		} else if (VideoPlayerHtml5.isPlayable(url)) {
			this.player = new VideoPlayerHtml5(this.element, this.modalService);
			this.type = 'html5';
		} else {
			// not playable
			this.type = 'invalid';
			// console.error('unplayable video: ', url);
			return Promise.reject();
		}
		let onEndPromise = this.player.play(url);
		// console.log('got onEndPromise', onEndPromise);
		// console.log('VideoPlayer setVolume', this.volume);
		this.player.setVolume(this.volume);
		return onEndPromise;
	}

	/**
	 * Suspend playback
	 */
	pause() {
		if (this.player) {
			this.player.pause();
		}
	}

	/**
	 * Stop playback and destroy the player and its html
	 */
	stop() {
		if (this.player) {
			this.player.stop();
		}
	}

	/**
	 * Change the volume
	 * @param {Number} volume  A number between 0 and 1
	 */
	setVolume(volume) {
		if (typeof volume == 'number' && volume >= 0 && volume <= 1) {
			this.volume = volume;
		}
		if (this.player) {
			this.player.setVolume(volume);
		}
	}
}
